import React from 'react';
import { Card, Row, Col, Typography, Badge, Tooltip, Empty } from 'antd';
import { TrophyOutlined, LockOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  .achievements-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 16px;
    margin-top: 16px;
  }
  .achievement-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 16px 8px;
    border-radius: 8px;
    transition: all 0.3s;
    cursor: pointer;

    &:hover {
      background: #f5f5f5;
      transform: translateY(-2px);
    }

    &.locked {
      opacity: 0.5;
    }

    &.locked .achievement-icon {
      filter: grayscale(100%);
    }
  }
  .achievement-icon {
    font-size: 48px;
    margin-bottom: 8px;
  }
  .achievement-name {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .achievement-points {
    font-size: 11px;
    color: #8c8c8c;
  }
  .rarity-badge {
    &.common { color: #8c8c8c; }
    &.rare { color: #1890ff; }
    &.epic { color: #722ed1; }
    &.legendary { color: #fa8c16; }
  }
`;

interface Achievement {
  id: string;
  key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  points_reward: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  earned: boolean;
  earned_at?: string;
}

interface AchievementBadgeProps {
  achievements: Achievement[];
  loading?: boolean;
  maxDisplay?: number;
}

const AchievementBadge: React.FC<AchievementBadgeProps> = ({
  achievements,
  loading = false,
  maxDisplay = 8
}) => {
  const displayAchievements = achievements.slice(0, maxDisplay);
  const earnedCount = achievements.filter(a => a.earned).length;

  return (
    <StyledCard
      bordered={false}
      loading={loading}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <TrophyOutlined /> Achievements
            </Title>
          </Col>
          <Col>
            <Text type="secondary">{earnedCount} / {achievements.length}</Text>
          </Col>
        </Row>
      }
    >
      {displayAchievements.length === 0 ? (
        <Empty description="No achievements yet" />
      ) : (
        <div className="achievements-grid">
          {displayAchievements.map((achievement) => (
            <Tooltip
              key={achievement.id}
              title={
                <div>
                  <div><strong>{achievement.name}</strong></div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>{achievement.description}</div>
                  <div style={{ fontSize: 11, marginTop: 4, color: '#faad14' }}>
                    +{achievement.points_reward} points
                  </div>
                  {achievement.earned && achievement.earned_at && (
                    <div style={{ fontSize: 11, marginTop: 4 }}>
                      Earned: {new Date(achievement.earned_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              }
            >
              <div className={`achievement-item ${!achievement.earned ? 'locked' : ''}`}>
                <div className="achievement-icon">
                  {achievement.earned ? achievement.icon : <LockOutlined />}
                </div>
                <div className="achievement-name">{achievement.name}</div>
                <div className={`achievement-points rarity-badge ${achievement.rarity}`}>
                  {achievement.rarity}
                </div>
              </div>
            </Tooltip>
          ))}
        </div>
      )}
    </StyledCard>
  );
};

export default AchievementBadge;
